import { useState } from "react";

import { MessageComposer, SendMessageValues } from "./MessageComposer";
import { MessagesList } from "./MessagesList";
import { ScheduledAgentMessage } from "./ScheduledAgentMessage";
import {
  Column,
  ConfirmationDialog,
  ErrorText,
  LoadingIndicator,
} from "@/components";
import { StafferAgentMessagingBanner } from "@jobs/staffer-agent/StafferAgentMessagingBanner";

import type { UserSheetCommsCandidacy } from "@/components/user-sheet/types";

import { useSendQuoMessage } from "@/hooks/mutations/useSendQuoMessage";
import { useUpdateCandidacy } from "@/hooks/mutations/useUpdateCandidacy";
import { useGetQuoMessages } from "@/hooks/queries/useGetQuoMessages";
import { useGetScheduledAgentMessages } from "@/hooks/queries/useGetScheduledAgentMessages";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { STAFFER_AGENT_TAKEOVER_DIALOG } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  CallHistoryAiCall,
  CallHistoryStafferCall,
  CandidacyStatus,
  StafferAgentScheduledMessageChannel,
} from "@/types";

export const StafferQuoMessages = ({
  aiCalls,
  autoFocus,
  calls,
  candidacy,
  isReadOnly = false,
  messageDraft,
  onMessageDraftChange,
  phone,
  talentId,
}: {
  // Optional: AI calls interweaved chronologically into the message list.
  aiCalls?: CallHistoryAiCall[];
  autoFocus?: boolean;
  // Optional: staffer calls interweaved chronologically into the message list.
  calls?: CallHistoryStafferCall[];
  candidacy?: UserSheetCommsCandidacy;
  // Viewing surfaces (search, application review) show the thread without any
  // way to text the candidate, so the composer and agent takeover drop out.
  isReadOnly?: boolean;
  // Optional: when the parent (e.g. the tabbed candidate sheet) owns the draft
  // so it survives tab switches. Standalone mounts fall back to local state.
  messageDraft?: string;
  onMessageDraftChange?: (body: string) => void;
  phone: string | null;
  talentId: number | null;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: messages,
    isLoading: isLoadingMessages,
    isError: isErrorGettingMessages,
  } = useGetQuoMessages({ talentId });

  const { data: scheduledMessages } = useGetScheduledAgentMessages({
    candidacyId: candidacy?.id ?? null,
  });

  // Create
  const sendMutation = useSendQuoMessage();

  // Update
  const { mutate: updateCandidacy } = useUpdateCandidacy();

  /* Cache Updates */
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();

  /* React Hooks */
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingSend, setPendingSend] = useState<SendMessageValues | null>(
    null,
  );
  // Fallback draft state for standalone mounts that don't lift the draft.
  const [localMessageDraft, setLocalMessageDraft] = useState("");
  const draft = messageDraft ?? localMessageDraft;
  const handleDraftChange = onMessageDraftChange ?? setLocalMessageDraft;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const agentActive = candidacy?.stafferAgentActive || false;
  const scheduledSms = scheduledMessages?.find(
    (scheduledMessage) =>
      scheduledMessage.channel === StafferAgentScheduledMessageChannel.SMS,
  );

  /* Handlers */
  // Bump pre-outreach candidacies into OUTREACH on first outbound message
  const advanceCandidacyToOutreachIfNeeded = () => {
    if (!candidacy) return;
    if (
      candidacy.status !== CandidacyStatus.APPLIED &&
      candidacy.status !== CandidacyStatus.SHORTLIST
    ) {
      return;
    }

    const lastCandidacyStatusChange = {
      fromStatus: candidacy.status,
      toStatus: CandidacyStatus.OUTREACH,
      statusChangeReason: null,
      byAgent: false,
    };

    setCandidacyInAllLists({
      candidacyId: candidacy.id,
      lastCandidacyStatusChange: {
        ...lastCandidacyStatusChange,
        createdAt: new Date().toISOString(),
      },
    });
    setCandidacyInfo({
      candidacyId: candidacy.id,
      lastCandidacyStatusChange,
      lastStatusChangeByAgent: false,
      status: CandidacyStatus.OUTREACH,
    });
  };

  const messagingScreen = MIXPANEL_SCREENS.CANDIDATE_SHEET;

  const performSend = (values: SendMessageValues) => {
    if (!phone) return;

    mixpanelUtils.trackFormSubmission("Send Message", messagingScreen, {
      candidacy_id: candidacy?.id,
      agent_was_active: agentActive,
    });

    advanceCandidacyToOutreachIfNeeded();

    sendMutation.mutate({
      sendQuoMessageRequest: {
        body: values.body,
        phone,
        talentId,
        candidacyId: candidacy?.id,
      },
    });
  };

  // Texting while the agent is active is an implicit takeover — confirm first.
  const handleSend = (values: SendMessageValues) => {
    if (!phone) return;

    if (agentActive) {
      setPendingSend(values);
      setIsConfirmOpen(true);
      return;
    }

    performSend(values);
  };

  const handleConfirmTakeOver = () => {
    mixpanelUtils.trackButtonClick("Take Over From Agent", messagingScreen, {
      candidacy_id: candidacy?.id,
      channel: "text",
      triggered_by: pendingSend ? "send" : "banner",
    });

    if (candidacy) {
      const bumpsToOutreach =
        pendingSend !== null &&
        (candidacy.status === CandidacyStatus.APPLIED ||
          candidacy.status === CandidacyStatus.SHORTLIST);

      updateCandidacy(
        {
          candidacyId: candidacy.id,
          updateCandidacyRequest: { stafferAgentActive: false },
        },
        {
          onSuccess: () => {
            setCandidacyInfo({
              candidacyId: candidacy.id,
              stafferAgentActive: false,
              ...(bumpsToOutreach && { status: CandidacyStatus.OUTREACH }),
            });
            setCandidacyInAllLists({
              candidacyId: candidacy.id,
              stafferAgentActive: false,
            });
          },
        },
      );
    }
    if (pendingSend) performSend(pendingSend);
    setPendingSend(null);
    setIsConfirmOpen(false);
  };

  /* Render */
  const render = () => {
    if (isLoadingMessages) {
      return <LoadingIndicator variant="medium" />;
    }

    if (isErrorGettingMessages || !messages) {
      return <ErrorText errorMessage="Error retrieving messages." />;
    }

    return (
      <Column className="h-full min-h-0 w-full">
        {candidacy && !isReadOnly && (
          <StafferAgentMessagingBanner
            active={agentActive}
            onTakeOver={() => {
              setIsConfirmOpen(true);
            }}
          />
        )}
        <MessagesList
          aiCalls={aiCalls}
          calls={calls}
          candidateFirstName={candidacy?.firstName ?? "Candidate"}
          footer={
            scheduledSms ? (
              <ScheduledAgentMessage scheduledMessage={scheduledSms} />
            ) : undefined
          }
          messages={messages}
        />
        {isReadOnly ? null : (
          <MessageComposer
            autoFocus={autoFocus}
            candidateName={`${candidacy?.firstName ?? ""} ${candidacy?.lastName ?? ""}`.trim()}
            messageDraft={draft}
            onMessageDraftChange={handleDraftChange}
            onSend={handleSend}
            phone={phone}
          />
        )}
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText={STAFFER_AGENT_TAKEOVER_DIALOG.confirmButtonText}
          description={STAFFER_AGENT_TAKEOVER_DIALOG.description}
          onConfirm={handleConfirmTakeOver}
          onOpenChange={(open) => {
            setIsConfirmOpen(open);
            if (!open) setPendingSend(null);
          }}
          open={isConfirmOpen}
          title={STAFFER_AGENT_TAKEOVER_DIALOG.title}
        />
      </Column>
    );
  };

  return render();
};
